import { Link, LinkProps } from 'react-router-dom';
import { prefetchRoute } from '@/lib/prefetch';
import { forwardRef, useCallback } from 'react';

/**
 * PrefetchLink - A Link component that prefetches route chunks on hover/focus
 * 
 * This improves perceived navigation speed by loading the destination
 * page's JavaScript before the user clicks.
 */
export const PrefetchLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, children, onMouseEnter, onFocus, onTouchStart, ...props }, ref) => {
    const handlePrefetch = useCallback(() => {
      if (typeof to === 'string') {
        prefetchRoute(to);
      } else if (typeof to === 'object' && to.pathname) {
        prefetchRoute(to.pathname);
      }
    }, [to]);

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        handlePrefetch();
        onMouseEnter?.(e);
      },
      [handlePrefetch, onMouseEnter]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLAnchorElement>) => {
        handlePrefetch();
        onFocus?.(e);
      },
      [handlePrefetch, onFocus]
    );

    const handleTouchStart = useCallback(
      (e: React.TouchEvent<HTMLAnchorElement>) => {
        handlePrefetch();
        onTouchStart?.(e);
      },
      [handlePrefetch, onTouchStart]
    );

    return (
      <Link
        ref={ref}
        to={to}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        onTouchStart={handleTouchStart}
        {...props}
      >
        {children}
      </Link>
    );
  }
);

PrefetchLink.displayName = 'PrefetchLink';
